<p-toast></p-toast>
<p-confirmDialog></p-confirmDialog>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<app-page-shell>

    <app-page-hero
        eyebrow="Fleet"
        title="Vehicles"
        subtitle="Manage owned and freelance vehicles, registrations, specifications, capabilities, and tracking links.">
        <div actions>
            <p-splitButton *ngIf="!isSisterMode" label="Add Vehicle" icon="fa-solid fa-plus" [model]="createVehicleItems" appendTo="body" (onClick)="addOwnVehicle()"></p-splitButton>
        </div>
    </app-page-hero>

    <app-page-search-panel
        title="Search Vehicles"
        [activeFilters]="activeVehicleFilterCount()"
        [resultText]="activeResultText()"
        [collapsed]="true"
        (search)="applyVehicleFilters()"
        (clear)="clearVehicleFilters()">
        <div class="formgrid grid">
            <div class="field col-12 md:col-4">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-search"></i></p-inputGroupAddon>
                    <p-iftalabel>
                        <input pInputText id="vehicleKeyword" type="text" [(ngModel)]="vehicleSearchText" placeholder="Make, model, payload, trip, or driver">
                        <label for="vehicleKeyword">Keyword Search</label>
                    </p-iftalabel>
                </p-inputGroup>
            </div>
            <div class="field col-12 md:col-2">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="fa-solid fa-hashtag"></i></p-inputGroupAddon>
                    <p-iftalabel>
                        <input pInputText id="vehiclePlate" type="text" [(ngModel)]="vehiclePlateSearch" placeholder="Plate #">
                        <label for="vehiclePlate">Plate #</label>
                    </p-iftalabel>
                </p-inputGroup>
            </div>
            <div class="field col-12 md:col-2">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="fa-solid fa-file-signature"></i></p-inputGroupAddon>
                    <p-iftalabel>
                        <input pInputText id="vehicleRegistration" type="text" [(ngModel)]="vehicleRegistrationSearch" placeholder="Registration #">
                        <label for="vehicleRegistration">Registration #</label>
                    </p-iftalabel>
                </p-inputGroup>
            </div>
            <div class="field col-12 md:col-2">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="fa-solid fa-gears"></i></p-inputGroupAddon>
                    <p-iftalabel>
                        <input pInputText id="vehicleEngine" type="text" [(ngModel)]="vehicleEngineSearch" placeholder="Engine #">
                        <label for="vehicleEngine">Engine #</label>
                    </p-iftalabel>
                </p-inputGroup>
            </div>
            <div class="field col-12 md:col-2">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="fa-solid fa-truck"></i></p-inputGroupAddon>
                    <p-iftalabel>
                        <input pInputText id="vehicleType" type="text" [(ngModel)]="vehicleTypeSearch" placeholder="Type, config, capacity">
                        <label for="vehicleType">Type</label>
                    </p-iftalabel>
                </p-inputGroup>
            </div>
            <div class="field col-12 md:col-2">
                <p-inputGroup>
                    <p-inputGroupAddon><i class="pi pi-circle-fill"></i></p-inputGroupAddon>
                    <p-iftalabel>
                        <p-select inputId="vehicleStatus" [options]="vehicleStatusOptions" optionLabel="label" optionValue="value"
                            [(ngModel)]="vehicleStatusSearch" [showClear]="true" appendTo="body" styleClass="w-full"></p-select>
                        <label for="vehicleStatus">Status</label>
                    </p-iftalabel>
                </p-inputGroup>
            </div>
        </div>
    </app-page-search-panel>

    <app-page-metric-strip [metrics]="vehicleMetrics()"></app-page-metric-strip>

    <app-page-data-panel
        title="Vehicles Directory"
        subtitle="Owned and freelance vehicle records available to fleet operations."
        [resultText]="activeResultText()">
        <div class="fleet-scope-toolbar">
            <div class="fleet-scope-switch" *ngIf="canUseSisterCompanyMode">
                <p-selectButton
                    [options]="resourceViewOptions"
                    optionLabel="label"
                    optionValue="value"
                    [ngModel]="resourceView"
                    (ngModelChange)="onResourceViewChange($event)">
                </p-selectButton>
            </div>
            <app-dashboard-company-scope-selector
                *ngIf="isSisterMode"
                [label]="'Sister company'"
                [companyGroupName]="sisterCompanyScope?.companyGroupName || ''"
                [memberCompanies]="sisterCompanies"
                [selectedCompanyId]="selectedSisterCompanyId"
                [allCompaniesLabel]="sisterCompanyAllLabel"
                [hint]="sisterCompanyFilterHint"
                (selectedCompanyIdChange)="onSisterCompanyChange($event)">
            </app-dashboard-company-scope-selector>
        </div>
        <p-tabs [value]="activeTabIndex" (valueChange)="tabChange($event)" styleClass="fleet-tabs">
            <p-tablist>
                <p-tab value="owned">
                    <span class="fleet-tab"><i class="pi pi-building"></i><span>Owned</span></span>
                </p-tab>
                <p-tab value="freelancer">
                    <span class="fleet-tab"><i class="pi pi-user"></i><span>Freelancers</span></span>
                </p-tab>
                <p-tab value="rental">
                    <span class="fleet-tab"><i class="pi pi-calendar"></i><span>Rental</span></span>
                </p-tab>
                <p-tab value="transporter">
                    <span class="fleet-tab"><i class="pi pi-building"></i><span>Transporter</span></span>
                </p-tab>
            </p-tablist>

            <p-tabpanels>
                <p-tabpanel value="owned">
                    <p-table [value]="ownedVehicles" dataKey="tableRowKey" [resizableColumns]="true"
                        styleClass="p-datatable-gridlines fleet-vehicle-table fleet-owned-table" [tableStyle]="{ 'width': '100%' }" [paginator]="true"
                        [rows]="10" [first]="ownedFirst" [showCurrentPageReport]="true" [totalRecords]="totalOwnedRecords"
                        [lazy]="true" currentPageReportTemplate="Showing {first} to {last} of {totalRecords} vehicles"
                        (onLazyLoad)="loadOwnedData($event)" [rowsPerPageOptions]="[10, 20, 50]">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>Registration</th>
                                <th>Vehicle Specs</th>
                                <th>Capabilities</th>
                                <th>Assigned Driver</th>
                                <th>Status</th>
                                <th class="text-right">Actions</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-vehicle>
                            <tr>
                                <td>
                                    <div class="fleet-cell-stack">
                                        <button type="button" class="fleet-link-button" (click)="viewVehicle(vehicle)">{{ vehicle.plateNumber || '-' }}</button>
                                        <small *ngIf="isSisterMode && vehicle.resourceCompanyName" class="fleet-resource-company">{{ vehicle.resourceCompanyName }}</small>
                                        <strong>{{ vehicle.registrationNumber || '-' }}</strong>
                                        <span>Engine: {{ vehicle.engineNumber || '-' }}</span>
                                        <span>Expiry: {{ vehicle.registrationExpiry | date: 'dd/MM/yyyy' }}</span>
                                    </div>
                                </td>
                                <td>
                                    <div class="fleet-spec-cell">
                                        <div class="fleet-inline-media" *ngIf="vehicle.type">
                                            <img [src]="vehicle.type.imageUrl" [alt]="vehicle.type.name || 'Vehicle type'" (error)="hideBrokenImage($event)" />
                                            <div class="fleet-cell-stack">
                                                <strong>{{ vehicle.type?.name || '-' }}</strong>
                                                <span>{{ vehicle.config?.name || '-' }} {{ vehicle.capacity?.name || '' }}</span>
                                            </div>
                                        </div>
                                        <div class="fleet-inline-media" *ngIf="vehicle.make">
                                            <img [src]="vehicle.make.imageUrl" [alt]="vehicle.make.name || 'Vehicle make'" (error)="hideBrokenImage($event)" />
                                            <div class="fleet-cell-stack">
                                                <strong>{{ vehicle.make?.name || '-' }} {{ vehicle.model?.name || '' }}</strong>
                                                <span>{{ vehicle.year?.year || '-' }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div class="fleet-meta-grid fleet-capabilities-grid">
                                        <div>
                                            <span>Payload</span>
                                            <div class="fleet-token-list" *ngIf="payloadTypeNames(vehicle).length; else noPayloadTypes">
                                                @for (payloadType of payloadTypeNames(vehicle); track payloadType) {
                                                <span class="fleet-token">{{ payloadType }}</span>
                                                }
                                            </div>
                                            <ng-template #noPayloadTypes><strong>-</strong></ng-template>
                                        </div>
                                        <div>
                                            <span>Trips</span>
                                            <div class="fleet-token-list" *ngIf="tripTypeNames(vehicle).length; else noTripTypes">
                                                @for (tripType of tripTypeNames(vehicle); track tripType) {
                                                <span class="fleet-token fleet-token-muted">{{ tripType }}</span>
                                                }
                                            </div>
                                            <ng-template #noTripTypes><strong>-</strong></ng-template>
                                        </div>
                                        <div>
                                            <span>Insurance</span>
                                            <strong>{{ vehicle.isInsured ? 'Insured' : 'Not insured' }}</strong>
                                            <small>{{ vehicle.insuranceExpiry | date:'dd/MM/yyyy' }}</small>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div class="fleet-cell-stack">
                                        <strong>{{ vehicle.currentDriver?.name || '-' }}</strong>
                                        <span>{{ vehicle.currentDriver?.phoneNumber || '' }}</span>
                                    </div>
                                </td>
                                <td>
                                    <div class="fleet-cell-stack">
                                        <p-tag [value]="vehicle.isActive === false ? 'Inactive' : 'Active'"
                                            [severity]="vehicleStatusSeverity(vehicle)"></p-tag>
                                        <p-select *ngIf="!isSisterMode" [options]="vehicleStatusOptions" [ngModel]="vehicle.isActive"
                                            optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"
                                            [disabled]="isVehicleStatusUpdating(vehicle)"
                                            (onChange)="updateVehicleStatus(vehicle, $event.value)"></p-select>
                                    </div>
                                </td>
                                <td>
                                    <div class="fleet-action-buttons">
                                        <button pButton type="button" icon="pi pi-eye" (click)="viewVehicle(vehicle)"
                                            class="p-button-rounded p-button-text p-button-help"></button>
                                        <button *ngIf="!isSisterMode" pButton type="button" icon="pi pi-sync" (click)="changeVehicleOwnership(vehicle)"
                                            class="p-button-rounded p-button-text p-button-warning" aria-label="Change ownership"></button>
                                        <button pButton type="button" icon="fa-solid fa-map-location" (click)="trackVehicle(vehicle)"
                                            class="p-button-rounded p-button-text p-button-success"></button>
                                        <button *ngIf="!isSisterMode" pButton type="button" icon="pi pi-pencil" (click)="editVehicle(vehicle)"
                                            class="p-button-rounded p-button-text p-button-secondary"></button>
                                        <button *ngIf="!isSisterMode" pButton type="button" icon="pi pi-trash" (click)="deleteVehicle(vehicle)"
                                            class="p-button-rounded p-button-text p-button-danger"></button>
                                    </div>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr>
                                <td colspan="6"><div class="fleet-empty-state">No owned vehicles found.</div></td>
                            </tr>
                        </ng-template>
                    </p-table>
                </p-tabpanel>

                <p-tabpanel value="freelancer">
                    <p-table [value]="freelancerVehicles" dataKey="tableRowKey" [resizableColumns]="true"
                        styleClass="p-datatable-gridlines fleet-vehicle-table fleet-owned-table" [tableStyle]="{ 'width': '100%' }" [paginator]="true"
                        [rows]="10" [first]="freelancerFirst" [showCurrentPageReport]="true"
                        [totalRecords]="totalFreelancerRecords" [lazy]="true"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} vehicles"
                        (onLazyLoad)="loadFreelancerData($event)" [rowsPerPageOptions]="[10, 20, 50]">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>Registration</th>
                                <th>Vehicle Specs</th>
                                <th>Capabilities</th>
                                <th>Assigned Driver</th>
                                <th>Status</th>
                                <th class="text-right">Actions</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-vehicle>
                            <tr>
                                <td>
                                    <div class="fleet-cell-stack">
                                        <button type="button" class="fleet-link-button" (click)="viewVehicle(vehicle)">{{ vehicle.plateNumber || '-' }}</button>
                                        <small *ngIf="isSisterMode && vehicle.resourceCompanyName" class="fleet-resource-company">{{ vehicle.resourceCompanyName }}</small>
                                        <strong>{{ vehicle.registrationNumber || '-' }}</strong>
                                        <span>Engine: {{ vehicle.engineNumber || '-' }}</span>
                                        <span>Expiry: {{ vehicle.registrationExpiry | date: 'dd/MM/yyyy' }}</span>
                                    </div>
                                </td>
                                <td>
                                    <div class="fleet-spec-cell">
                                        <div class="fleet-inline-media" *ngIf="vehicle.type">
                                            <img [src]="vehicle.type.imageUrl" [alt]="vehicle.type.name || 'Vehicle type'" (error)="hideBrokenImage($event)" />
                                            <div class="fleet-cell-stack">
                                                <strong>{{ vehicle.type?.name || '-' }}</strong>
                                                <span>{{ vehicle.config?.name || '-' }} {{ vehicle.capacity?.name || '' }}</span>
                                            </div>
                                        </div>
                                        <div class="fleet-inline-media" *ngIf="vehicle.make">
                                            <img [src]="vehicle.make.imageUrl" [alt]="vehicle.make.name || 'Vehicle make'" (error)="hideBrokenImage($event)" />
                                            <div class="fleet-cell-stack">
                                                <strong>{{ vehicle.make?.name || '-' }} {{ vehicle.model?.name || '' }}</strong>
                                                <span>{{ vehicle.year?.year || '-' }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div class="fleet-meta-grid fleet-capabilities-grid">
                                        <div>
                                            <span>Payload</span>
                                            <div class="fleet-token-list" *ngIf="payloadTypeNames(vehicle).length; else noFreelancePayloadTypes">
                                                @for (payloadType of payloadTypeNames(vehicle); track payloadType) {
                                                <span class="fleet-token">{{ payloadType }}</span>
                                                }
                                            </div>
                                            <ng-template #noFreelancePayloadTypes><strong>-</strong></ng-template>
                                        </div>
                                        <div>
                                            <span>Trips</span>
                                            <div class="fleet-token-list" *ngIf="tripTypeNames(vehicle).length; else noFreelanceTripTypes">
                                                @for (tripType of tripTypeNames(vehicle); track tripType) {
                                                <span class="fleet-token fleet-token-muted">{{ tripType }}</span>
                                                }
                                            </div>
                                            <ng-template #noFreelanceTripTypes><strong>-</strong></ng-template>
                                        </div>
                                        <div>
                                            <span>Insurance</span>
                                            <strong>{{ vehicle.isInsured ? 'Insured' : 'Not insured' }}</strong>
                                            <small>{{ vehicle.insuranceExpiry | date:'dd/MM/yyyy' }}</small>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div class="fleet-cell-stack">
                                        <strong>{{ vehicle.currentDriver?.name || '-' }}</strong>
                                        <span>{{ vehicle.currentDriver?.phoneNumber || '' }}</span>
                                    </div>
                                </td>
                                <td>
                                    <div class="fleet-cell-stack">
                                        <p-tag [value]="vehicle.isActive === false ? 'Inactive' : 'Active'"
                                            [severity]="vehicleStatusSeverity(vehicle)"></p-tag>
                                        <p-select *ngIf="!isSisterMode" [options]="vehicleStatusOptions" [ngModel]="vehicle.isActive"
                                            optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"
                                            [disabled]="isVehicleStatusUpdating(vehicle)"
                                            (onChange)="updateVehicleStatus(vehicle, $event.value)"></p-select>
                                    </div>
                                </td>
                                <td>
                                    <div class="fleet-action-buttons">
                                        <button pButton type="button" icon="pi pi-eye" (click)="viewVehicle(vehicle)"
                                            class="p-button-rounded p-button-text p-button-help"></button>
                                        <button *ngIf="!isSisterMode" pButton type="button" icon="pi pi-sync" (click)="changeVehicleOwnership(vehicle)"
                                            class="p-button-rounded p-button-text p-button-warning" aria-label="Change ownership"></button>
                                        <button pButton type="button" icon="fa-solid fa-map-location" (click)="trackVehicle(vehicle)"
                                            class="p-button-rounded p-button-text p-button-success"></button>
                                        <button *ngIf="!isSisterMode" pButton type="button" icon="pi pi-pencil" (click)="editVehicle(vehicle)"
                                            class="p-button-rounded p-button-text p-button-secondary"></button>
                                        <button *ngIf="!isSisterMode" pButton type="button" icon="pi pi-trash" (click)="deleteVehicle(vehicle)"
                                            class="p-button-rounded p-button-text p-button-danger"></button>
                                    </div>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr>
                                <td colspan="6"><div class="fleet-empty-state">No freelance vehicles found.</div></td>
                            </tr>
                        </ng-template>
                    </p-table>
                </p-tabpanel>
                <p-tabpanel value="rental">
                    <p-table [value]="rentalVehicles" dataKey="tableRowKey" [resizableColumns]="true"
                        styleClass="p-datatable-gridlines fleet-vehicle-table fleet-rental-table" [tableStyle]="{ 'width': '100%' }" [paginator]="true"
                        [rows]="10" [first]="rentalFirst" [showCurrentPageReport]="true"
                        [totalRecords]="totalRentalRecords" [lazy]="true"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} vehicles"
                        (onLazyLoad)="loadRentalData($event)" [rowsPerPageOptions]="[10, 20, 50]">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>Registration</th>
                                <th>Vehicle Specs</th>
                                <th>Assigned Driver</th>
                                <th>Rental Terms</th>
                                <th>Rental Status</th>
                                <th>Fleet Status</th>
                                <th class="text-right">Actions</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-vehicle>
                            <tr>
                                <td>
                                    <div class="fleet-cell-stack">
                                        <button type="button" class="fleet-link-button" (click)="viewVehicle(vehicle)">{{ vehicle.plateNumber || '-' }}</button>
                                        <small *ngIf="isSisterMode && vehicle.resourceCompanyName" class="fleet-resource-company">{{ vehicle.resourceCompanyName }}</small>
                                        <strong>{{ vehicle.registrationNumber || '-' }}</strong>
                                        <span>Engine: {{ vehicle.engineNumber || '-' }}</span>
                                        <span>Expiry: {{ vehicle.registrationExpiry | date: 'dd/MM/yyyy' }}</span>
                                    </div>
                                </td>
                                <td>
                                    <div class="fleet-spec-cell">
                                        <div class="fleet-inline-media" *ngIf="vehicle.type">
                                            <img [src]="vehicle.type.imageUrl" [alt]="vehicle.type.name || 'Vehicle type'" (error)="hideBrokenImage($event)" />
                                            <div class="fleet-cell-stack">
                                                <strong>{{ vehicle.type?.name || '-' }}</strong>
                                                <span>{{ vehicle.config?.name || '-' }} {{ vehicle.capacity?.name || '' }}</span>
                                            </div>
                                        </div>
                                        <div class="fleet-inline-media" *ngIf="vehicle.make">
                                            <img [src]="vehicle.make.imageUrl" [alt]="vehicle.make.name || 'Vehicle make'" (error)="hideBrokenImage($event)" />
                                            <div class="fleet-cell-stack">
                                                <strong>{{ vehicle.make?.name || '-' }} {{ vehicle.model?.name || '' }}</strong>
                                                <span>{{ vehicle.year?.year || '-' }}</span>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div class="fleet-cell-stack">
                                        <strong>{{ vehicle.currentDriver?.name || '-' }}</strong>
                                        <span>{{ vehicle.currentDriver?.phoneNumber || '' }}</span>
                                    </div>
                                </td>
                                <td>
                                    <div class="fleet-cell-stack">
                                        <strong>{{ vehicle.rentalVendorName || '-' }}</strong>
                                        <span>Ref: {{ vehicle.rentalContractReferenceNumber || '-' }}</span>
                                        <span>{{ vehicle.rentalStartDate | date:'dd/MM/yyyy' }} - {{ vehicle.rentalEndDate | date:'dd/MM/yyyy' }}</span>
                                        <span>{{ vehicle.rentalRateAmount || '-' }} {{ vehicle.rentalRateCurrency || 'SAR' }} / {{ vehicle.rentalRatePeriod || 'DAY' }}</span>
                                    </div>
                                </td>
                                <td>
                                    <p-tag [value]="isRentalExpired(vehicle) ? 'Expired' : (vehicle.rentalStatus || 'ACTIVE')"
                                        [severity]="isRentalExpired(vehicle) ? 'danger' : 'success'"></p-tag>
                                </td>
                                <td>
                                    <div class="fleet-cell-stack">
                                        <p-tag [value]="vehicle.isActive === false ? 'Inactive' : 'Active'"
                                            [severity]="vehicleStatusSeverity(vehicle)"></p-tag>
                                        <p-select *ngIf="!isSisterMode" [options]="vehicleStatusOptions" [ngModel]="vehicle.isActive"
                                            optionLabel="label" optionValue="value" appendTo="body" styleClass="w-full"
                                            [disabled]="isVehicleStatusUpdating(vehicle)"
                                            (onChange)="updateVehicleStatus(vehicle, $event.value)"></p-select>
                                    </div>
                                </td>
                                <td>
                                    <div class="fleet-action-buttons">
                                        <button pButton type="button" icon="pi pi-eye" (click)="viewVehicle(vehicle)"
                                            class="p-button-rounded p-button-text p-button-help"></button>
                                        <button *ngIf="!isSisterMode" pButton type="button" icon="pi pi-sync" (click)="changeVehicleOwnership(vehicle)"
                                            class="p-button-rounded p-button-text p-button-warning" aria-label="Change ownership"></button>
                                        <button pButton type="button" icon="fa-solid fa-map-location" (click)="trackVehicle(vehicle)"
                                            class="p-button-rounded p-button-text p-button-success"></button>
                                        <button *ngIf="!isSisterMode" pButton type="button" icon="pi pi-pencil" (click)="editVehicle(vehicle)"
                                            class="p-button-rounded p-button-text p-button-secondary"></button>
                                        <button *ngIf="!isSisterMode" pButton type="button" icon="pi pi-trash" (click)="deactivateRentalVehicle(vehicle)"
                                            class="p-button-rounded p-button-text p-button-danger"></button>
                                    </div>
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage">
                            <tr>
                                <td colspan="7"><div class="fleet-empty-state">No rental vehicles found.</div></td>
                            </tr>
                        </ng-template>
                    </p-table>
                </p-tabpanel>
                <p-tabpanel value="transporter">
                    <div class="formgrid grid mb-3">
                        <div class="field col-12 md:col-6">
                            <p-inputGroup>
                                <p-inputGroupAddon><i class="fa-solid fa-building-truck"></i></p-inputGroupAddon>
                                <p-iftalabel>
                                    <p-select inputId="vehicleTransporter" [options]="transporterOptions"
                                        optionLabel="transporterName" optionValue="crmClientId"
                                        [ngModel]="selectedTransporterCrmClientId"
                                        (ngModelChange)="onTransporterChange($event)" [showClear]="true"
                                        [filter]="true" filterBy="transporterName,crmClientNumber,linkedCompanyName"
                                        appendTo="body" styleClass="w-full"></p-select>
                                    <label for="vehicleTransporter">Transporter agency</label>
                                </p-iftalabel>
                            </p-inputGroup>
                            <small class="text-color-secondary">Select a CRM transporter to view only its agency vehicles.</small>
                        </div>
                    </div>
                    <p-table [value]="transporterVehicles" dataKey="tableRowKey" [resizableColumns]="true"
                        styleClass="p-datatable-gridlines fleet-vehicle-table fleet-owned-table" [tableStyle]="{ 'width': '100%' }" [paginator]="true"
                        [rows]="transporterRows" [first]="transporterFirst" [showCurrentPageReport]="true"
                        [totalRecords]="totalTransporterRecords" [lazy]="true"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} transporter vehicles"
                        (onLazyLoad)="loadTransporterData($event)" [rowsPerPageOptions]="[10, 20, 50]">
                        <ng-template pTemplate="header"><tr><th>Registration</th><th>Transporter</th><th>Vehicle Specs</th><th>Assigned Driver</th><th>Status</th><th class="text-right">Actions</th></tr></ng-template>
                        <ng-template pTemplate="body" let-vehicle>
                            <tr>
                                <td><div class="fleet-cell-stack"><button type="button" class="fleet-link-button" (click)="viewVehicle(vehicle)">{{ vehicle.plateNumber || '-' }}</button><strong>{{ vehicle.registrationNumber || '-' }}</strong><span>Engine: {{ vehicle.engineNumber || '-' }}</span></div></td>
                                <td><div class="fleet-cell-stack"><strong>{{ selectedTransporterName }}</strong><span>{{ vehicle.resourceCompanyName || 'Transporter agency' }}</span></div></td>
                                <td><div class="fleet-cell-stack"><strong>{{ vehicle.type?.name || '-' }}</strong><span>{{ vehicle.config?.name || '-' }} {{ vehicle.capacity?.name || '' }}</span><span>{{ vehicle.make?.name || '-' }} {{ vehicle.model?.name || '' }}</span></div></td>
                                <td><div class="fleet-cell-stack"><strong>{{ vehicle.currentDriver?.name || '-' }}</strong><span>{{ vehicle.currentDriver?.phoneNumber || '' }}</span></div></td>
                                <td><p-tag [value]="vehicle.isActive === false ? 'Inactive' : 'Active'" [severity]="vehicleStatusSeverity(vehicle)"></p-tag></td>
                                <td><div class="fleet-action-buttons"><button pButton type="button" icon="pi pi-eye" (click)="viewVehicle(vehicle)" class="p-button-rounded p-button-text p-button-help"></button><button *ngIf="!isSisterMode" pButton type="button" icon="pi pi-sync" (click)="changeVehicleOwnership(vehicle)" class="p-button-rounded p-button-text p-button-warning" aria-label="Change ownership"></button><button pButton type="button" icon="fa-solid fa-map-location" (click)="trackVehicle(vehicle)" class="p-button-rounded p-button-text p-button-success"></button></div></td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage"><tr><td colspan="6"><div class="fleet-empty-state">Select a transporter to view its vehicles.</div></td></tr></ng-template>
                    </p-table>
                </p-tabpanel>
            </p-tabpanels>
        </p-tabs>
    </app-page-data-panel>
</app-page-shell>

<app-quick-fleet-onboarding-dialog [(visible)]="quickFleetOnboardingVisible" [defaultSource]="quickFleetOnboardingSource" [defaultTransporterCrmClientId]="selectedTransporterCrmClientId" (completed)="onQuickFleetOnboarded()"></app-quick-fleet-onboarding-dialog>
<app-fleet-ownership-transfer-dialog [(visible)]="ownershipTransferVisible" [owningCompanyId]="currentCompanyId"
    [resources]="ownershipTransferResources" [pairedResource]="ownershipTransferPairedResource"
    (completed)="onOwnershipTransferCompleted()"></app-fleet-ownership-transfer-dialog>

<p-dialog [(visible)]="showAddFreelancerVehicleDialog" header="Add Freelancer Vehicle" [modal]="true"
    [style]="{ width: 'min(92vw, 42rem)' }" [draggable]="false" [resizable]="false">
    <div class="p-fluid">
        <div class="field">
            <p-inputGroup>
                <p-inputGroupAddon><i class="fa-solid fa-truck"></i></p-inputGroupAddon>
                <p-iftalabel>
                    <input id="registrationNumber" type="text" pInputText [(ngModel)]="vehicleRegistrationNumber" required />
                    <label for="registrationNumber">Vehicle Registration Number</label>
                </p-iftalabel>
            </p-inputGroup>
        </div>
    </div>
    <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="fa-solid fa-close" severity="secondary" [outlined]="true"
            (onClick)="showAddFreelancerVehicleDialog = false"></p-button>
        <p-button label="Search Vehicle" icon="fa-solid fa-search" (onClick)="searchVehicle()"
            [loading]="searchVehicleInProgress"></p-button>
    </ng-template>
</p-dialog>
